import { useMemo, useState, type ReactNode } from 'react';
import { toast } from 'sonner';
import { ArrowDown, ArrowUp, Loader2, Plus, Trash2, AlertTriangle, ShieldAlert } from 'lucide-react';
import { apiFetch } from '@/lib/api-client';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Textarea } from '@/components/ui/textarea';
import { ConfirmDialog } from '@/components/ui/confirm-dialog';
import {
  TUTORIAL_CALLOUT_TYPES, TUTORIAL_CARD_KINDS, TUTORIAL_CODE_GROUPS, TUTORIAL_PHASES, TUTORIAL_SCOPE_OUTCOMES,
  TUTORIAL_TAGS, emptyTutorialCard,
} from '@uniticket/shared';
import type {
  SaveTutorialNodeRequest, TutorialAnswer, TutorialAnswerMatch, TutorialCallout, TutorialCard, TutorialCardKind,
  TutorialCalloutType, TutorialChecklistItem, TutorialCodeGroup, TutorialNodeDto, TutorialPhase, TutorialScopeOutcome,
  TutorialTag,
} from '@uniticket/shared';

// N32.6 (plan validé le 29-09-2026) — les admins corrigent le parcours SUR LA CARTE (choix du quiz du 29-09), et un
// panneau « Contrôle » leur dit où le graphe a des trous. La garde qui compte reste celle du serveur : une route
// d'écriture refuse un validateur même si ce formulaire se retrouvait à l'écran.
//
// Le formulaire tient la carte ENTIÈRE et l'envoie en un bloc (le serveur la valide champ par champ). Les listes de
// texte — points, étapes — s'écrivent une ligne par élément : c'est la façon la plus simple de relire et de réordonner.
// Les RÉPONSES, elles, ont leur ordre qui compte (la première qui correspond au code saisi l'emporte) : on les
// monte et on les descend.

type Lang = 'en' | 'fr';

const E = {
  fr: {
    title: 'Modifier cette étape',
    identity: 'L’étape',
    titleLabel: 'Titre (la question, ou ce que dit la carte)',
    shortLabel: 'Libellé court (fil et rail)',
    keyLabel: 'Clé (identifiant stable — minuscules, chiffres, tirets)',
    phaseLabel: 'Phase',
    kindLabel: 'Type de carte',
    rootLabel: 'Entrée du parcours',
    orderLabel: 'Ordre dans la phase',
    tagsLabel: 'Marques',
    leadLabel: 'Introduction',
    pointsLabel: 'Points (un par ligne)',
    stepsLabel: 'Étapes numérotées (une par ligne)',
    calloutsLabel: 'Encadrés',
    addCallout: 'Ajouter un encadré',
    moreLabel: '« En savoir plus » (replié par défaut)',
    moreTitleLabel: 'Titre du dépliant (vide = « En savoir plus »)',
    checklistLabel: 'Check-list',
    addCheck: 'Ajouter une case',
    required: 'obligatoire',
    checklistHintLabel: 'Phrase sous la check-list',
    examplesLabel: 'Codes proposés en exemple (4 chiffres, séparés par des virgules)',
    windowLabel: 'Aperçu de fenêtre',
    windowBar: 'Barre',
    windowTitle: 'Titre de la fenêtre',
    windowText: 'Texte de la fenêtre',
    nextLabel: 'Bouton « suivant »',
    nextOn: 'La carte a un bouton « suivant »',
    nextText: 'Libellé du bouton',
    chipLabel: 'Étiquette dans le fil (vide = le libellé)',
    distanceLabel: 'Afficher l’aide à la distance',
    skipLabel: 'Passer cette carte d’office quand le code de livraison désigne une réponse',
    skipHint: 'La carte est sautée quand on y arrive avec un code décodé qui désigne UNE SEULE de ses réponses (leur critère « cadran », « groupe » ou « code »). Sans code, ou si plusieurs réponses correspondent, elle s’affiche. Revenir en arrière l’affiche toujours.',
    factLabel: 'Inscrire la réponse dans « ce que vous avez relevé », sous le libellé :',
    answersLabel: 'Réponses',
    answersHint: 'L’ordre compte : sur une carte « code » ou « scope », et pour les suggestions, la première réponse qui correspond l’emporte.',
    addAnswer: 'Ajouter une réponse',
    answerLabel: 'Libellé',
    hintLabel: 'Aide sous le libellé',
    targetLabel: 'Mène à',
    noTarget: '— pas encore câblée —',
    newStep: '➕ Nouvelle étape…',
    primary: 'bouton principal',
    matchLabel: 'Correspondance',
    matchHint: 'Ce qui fait choisir (cartes « code » et « scope ») ou suggérer cette réponse.',
    matchGroups: 'Groupe du code',
    matchQuadrants: 'Cadrans (séparés par des virgules)',
    matchCodes: 'Codes exacts (séparés par des virgules)',
    matchScope: 'Issue du scope',
    commentLabel: 'Réponse type à copier dans le ticket',
    referenceLabel: 'Règle de référence et date',
    save: 'Enregistrer',
    saving: 'Enregistrement…',
    saved: 'Étape enregistrée.',
    cancel: 'Annuler',
    deleteStep: 'Supprimer l’étape',
    deleteTitle: 'Supprimer cette étape ?',
    deleteBody: 'L’étape est supprimée. Les réponses et les boutons « suivant » qui y menaient restent, sans destination : le panneau « Contrôle » les listera pour que vous les recâbliez.',
    deleted: 'Étape supprimée — {n} lien(s) débranché(s).',
    created: 'Étape créée : {key}. Enregistrez pour la câbler.',
    newStepTitle: 'Nouvelle étape',
    updatedBy: 'Dernière modification',
    remove: 'Retirer',
    up: 'Monter',
    down: 'Descendre',
    control: 'Contrôle',
    controlTitle: 'Contrôle du parcours',
    controlOk: 'Aucune anomalie : toutes les étapes sont joignables et toutes les issues sont câblées.',
    unreachable: 'injoignable depuis les entrées',
    unwired: 'réponse « {label} » : pas encore câblée',
    missing: 'réponse « {label} » : mène à « {target} », qui n’existe pas',
    nextUnwired: 'bouton « suivant » : pas encore câblé',
    nextMissing: 'bouton « suivant » : mène à « {target} », qui n’existe pas',
    deadEnd: 'carte sans issue (ni réponse, ni « suivant ») qui n’est pas une fin',
    codeGap: 'carte « code » : aucun routage pour {groups}',
    scopeGap: 'carte « scope » : aucune issue pour {outcomes}',
    skipNoCriteria: 'carte « à passer d’office » dont aucune réponse ne porte de critère de code : elle ne sera jamais sautée',
    open: 'Ouvrir',
  },
  en: {
    title: 'Edit this step',
    identity: 'The step',
    titleLabel: 'Title (the question, or what the card says)',
    shortLabel: 'Short label (trail and rail)',
    keyLabel: 'Key (stable identifier — lowercase, digits, hyphens)',
    phaseLabel: 'Phase',
    kindLabel: 'Card type',
    rootLabel: 'Entry of the path',
    orderLabel: 'Order within the phase',
    tagsLabel: 'Marks',
    leadLabel: 'Introduction',
    pointsLabel: 'Points (one per line)',
    stepsLabel: 'Numbered steps (one per line)',
    calloutsLabel: 'Callouts',
    addCallout: 'Add a callout',
    moreLabel: '“Learn more” (folded by default)',
    moreTitleLabel: 'Fold title (empty = “Learn more”)',
    checklistLabel: 'Checklist',
    addCheck: 'Add a box',
    required: 'required',
    checklistHintLabel: 'Line under the checklist',
    examplesLabel: 'Example codes (4 digits, comma-separated)',
    windowLabel: 'Window preview',
    windowBar: 'Bar',
    windowTitle: 'Window title',
    windowText: 'Window text',
    nextLabel: '“Next” button',
    nextOn: 'The card has a “next” button',
    nextText: 'Button label',
    chipLabel: 'Trail label (empty = the label)',
    distanceLabel: 'Show the distance helper',
    skipLabel: 'Skip this card automatically when the delivery code points to an answer',
    skipHint: 'The card is skipped when you reach it with a decoded code that points to ONE of its answers only (their “quadrant”, “group” or “code” criterion). With no code, or when several answers match, it is shown. Going back always shows it.',
    factLabel: 'Record the answer in “what you noted”, under the label:',
    answersLabel: 'Answers',
    answersHint: 'Order matters: on a “code” or “scope” card, and for suggestions, the first matching answer wins.',
    addAnswer: 'Add an answer',
    answerLabel: 'Label',
    hintLabel: 'Help under the label',
    targetLabel: 'Leads to',
    noTarget: '— not wired yet —',
    newStep: '➕ New step…',
    primary: 'main button',
    matchLabel: 'Match',
    matchHint: 'What makes this answer chosen (“code” and “scope” cards) or suggested.',
    matchGroups: 'Code group',
    matchQuadrants: 'Quadrants (comma-separated)',
    matchCodes: 'Exact codes (comma-separated)',
    matchScope: 'Scope outcome',
    commentLabel: 'Ready-made reply to copy into the ticket',
    referenceLabel: 'Reference rule and date',
    save: 'Save',
    saving: 'Saving…',
    saved: 'Step saved.',
    cancel: 'Cancel',
    deleteStep: 'Delete the step',
    deleteTitle: 'Delete this step?',
    deleteBody: 'The step is deleted. Answers and “next” buttons that led to it stay, without a destination: the “Check” panel will list them so you can rewire them.',
    deleted: 'Step deleted — {n} link(s) unwired.',
    created: 'Step created: {key}. Save to wire it.',
    newStepTitle: 'New step',
    updatedBy: 'Last modified',
    remove: 'Remove',
    up: 'Move up',
    down: 'Move down',
    control: 'Check',
    controlTitle: 'Path check',
    controlOk: 'No anomaly: every step is reachable and every outcome is wired.',
    unreachable: 'unreachable from the entries',
    unwired: 'answer “{label}”: not wired yet',
    missing: 'answer “{label}”: leads to “{target}”, which does not exist',
    nextUnwired: '“next” button: not wired yet',
    nextMissing: '“next” button: leads to “{target}”, which does not exist',
    deadEnd: 'card with no way out (no answer, no “next”) that is not an end',
    codeGap: '“code” card: no route for {groups}',
    scopeGap: '“scope” card: no outcome for {outcomes}',
    skipNoCriteria: '“skip automatically” card none of whose answers carries a code criterion: it will never be skipped',
    open: 'Open',
  },
} as const;
type ELabels = { [K in keyof typeof E['fr']]: string };
const fill = (s: string, v: Record<string, string | number>) => s.replace(/\{(\w+)\}/g, (_, k) => String(v[k] ?? ''));

const KIND_LABEL: Record<Lang, Record<TutorialCardKind, string>> = {
  fr: { question: 'Question', info: 'Information', action: 'Action', end: 'Fin de parcours', code: 'Code de livraison', scope: 'Scope (trois cases)', window: 'Aperçu de fenêtre', rules: 'Règles de capacité (hors parcours)' },
  en: { question: 'Question', info: 'Information', action: 'Action', end: 'End of path', code: 'Delivery code', scope: 'Scope (three boxes)', window: 'Window preview', rules: 'Capacity rules (off the path)' },
};
const PHASE_SHORT: Record<Lang, Record<TutorialPhase, string>> = {
  fr: { lire: 'Lire', identifier: 'Identifier', verifier: 'Vérifier', capacite: 'Capacité', agir: 'Agir', terminer: 'Terminer' },
  en: { lire: 'Read', identifier: 'Identify', verifier: 'Check', capacite: 'Capacity', agir: 'Act', terminer: 'Finish' },
};
const TAG_SHORT: Record<Lang, Record<TutorialTag, string>> = {
  fr: { todo: 'À définir', decide: 'À trancher', meeting: 'Retour du 24-09', future: 'Pas encore codé' },
  en: { todo: 'To be defined', decide: 'To be settled', meeting: '24-09 feedback', future: 'Not coded yet' },
};
const CALLOUT_SHORT: Record<Lang, Record<TutorialCalloutType, string>> = {
  fr: { todo: 'À définir', decide: 'À trancher', warn: 'Attention', ok: 'Neutre (vert)' },
  en: { todo: 'To be defined', decide: 'To be settled', warn: 'Watch out', ok: 'Neutral (green)' },
};
const GROUP_SHORT: Record<Lang, Record<TutorialCodeGroup, string>> = {
  fr: { HP_HCA: 'Homes Passed', NOT_HP: 'pas encore HP', HC: 'Homes Connect', BLOCKED: 'blocage', NONE: 'aucun code' },
  en: { HP_HCA: 'Homes Passed', NOT_HP: 'not HP yet', HC: 'Homes Connect', BLOCKED: 'blocking', NONE: 'no code' },
};
const SCOPE_SHORT: Record<Lang, Record<TutorialScopeOutcome, string>> = {
  fr: { both: 'les deux listes', v2: 'V2 seule', v15: 'V1.5 seule', none: 'aucune liste', oos: 'retirée' },
  en: { both: 'both lists', v2: 'V2 only', v15: 'V1.5 only', none: 'no list', oos: 'removed' },
};

const lines = (s: string) => s.split('\n').map((l) => l.trim()).filter(Boolean);
const list = (s: string) => s.split(/[\s,;]+/).map((x) => x.trim()).filter(Boolean);
const nullIfBlank = (s: string | null | undefined) => (s && s.trim() ? s.trim() : null);

/** Un champ étiqueté. Défini au niveau du MODULE et non dans le composant d'édition : une fonction composant
 *  recréée à chaque rendu est un type neuf pour React, qui démonte alors le sous-arbre — et l'input perdrait le
 *  focus à chaque caractère tapé. */
function Field({ label, children, hint }: { label: string; children: ReactNode; hint?: string }) {
  return (
    <label className="block">
      <span className="mb-1 block text-[11px] font-semibold text-slate-500">{label}</span>
      {children}
      {hint && <span className="mt-1 block text-[11px] text-slate-400">{hint}</span>}
    </label>
  );
}
function Section({ title, children }: { title: string; children: ReactNode }) {
  return (
    <fieldset className="space-y-3 rounded-xl border border-slate-200 p-3.5">
      <legend className="px-1 text-[11px] font-bold uppercase tracking-wider text-slate-400">{title}</legend>
      {children}
    </fieldset>
  );
}
const SELECT_CLASS = 'h-9 w-full rounded-md border border-slate-200 bg-white px-2 text-[13px] text-slate-700';

/** Les cibles possibles, groupées par phase — plus « pas encore câblée » et « nouvelle étape ». */
function TargetSelect({
  value, onChange, nodes, onCreate, lang, el,
}: {
  value: string | null;
  onChange: (key: string | null) => void;
  nodes: TutorialNodeDto[];
  onCreate: () => void;
  lang: Lang;
  el: ELabels;
}) {
  const missing = value != null && !nodes.some((n) => n.key === value);
  return (
    <select
      value={value ?? ''}
      onChange={(e) => {
        if (e.target.value === '__new__') { onCreate(); return; }
        onChange(e.target.value || null);
      }}
      className={SELECT_CLASS}
      aria-label={el.targetLabel}
    >
      <option value="">{el.noTarget}</option>
      {missing && <option value={value}>{value} (?)</option>}
      {TUTORIAL_PHASES.map((p) => (
        <optgroup key={p} label={PHASE_SHORT[lang][p]}>
          {nodes.filter((n) => n.category === p && n.card.kind !== 'rules').sort((a, b) => a.sortOrder - b.sortOrder).map((n) => (
            <option key={n.key} value={n.key}>{(n.card.short || n.title).slice(0, 60)} · {n.key}</option>
          ))}
        </optgroup>
      ))}
      <option value="__new__">{el.newStep}</option>
    </select>
  );
}

export function CardEditor({
  node, nodes, lang, onSaved, onDeleted, onCancel,
}: {
  node: TutorialNodeDto;
  nodes: TutorialNodeDto[];
  lang: Lang;
  /** Après l'enregistrement : l'ancienne clé et la nouvelle (un renommage doit garder le lecteur sur la carte). */
  onSaved: (oldKey: string, newKey: string) => Promise<void>;
  onDeleted: (key: string) => Promise<void>;
  onCancel: () => void;
}) {
  const el: ELabels = E[lang];
  const [title, setTitle] = useState(node.title);
  const [key, setKey] = useState(node.key);
  const [phase, setPhase] = useState<TutorialPhase>((TUTORIAL_PHASES as readonly string[]).includes(node.category) ? node.category as TutorialPhase : 'lire');
  const [isRoot, setIsRoot] = useState(node.isRoot);
  const [sortOrder, setSortOrder] = useState(node.sortOrder);
  const [comment, setComment] = useState(node.comment ?? '');
  const [reference, setReference] = useState(node.reference ?? '');
  const [card, setCard] = useState<TutorialCard>({ ...emptyTutorialCard(), ...node.card });
  const [pointsText, setPointsText] = useState(node.card.points.join('\n'));
  const [stepsText, setStepsText] = useState(node.card.steps.join('\n'));
  const [examplesText, setExamplesText] = useState(node.card.examples.join(', '));
  const [answers, setAnswers] = useState<TutorialAnswer[]>(node.answers.map((a) => ({ ...a })));
  const [extraNodes, setExtraNodes] = useState<TutorialNodeDto[]>([]);
  const [saving, setSaving] = useState(false);
  const [confirmDelete, setConfirmDelete] = useState(false);
  const targets = useMemo(() => [...nodes, ...extraNodes.filter((x) => !nodes.some((n) => n.key === x.key))], [nodes, extraNodes]);

  const setC = <K extends keyof TutorialCard>(k: K, v: TutorialCard[K]) => setCard((c) => ({ ...c, [k]: v }));
  const setA = (i: number, patch: Partial<TutorialAnswer>) => setAnswers((as) => as.map((a, j) => (j === i ? { ...a, ...patch } : a)));
  const setM = (i: number, patch: Partial<TutorialAnswerMatch>) => setAnswers((as) => as.map((a, j) => (j === i ? { ...a, match: { ...(a.match ?? {}), ...patch } } : a)));
  const move = (i: number, d: -1 | 1) => setAnswers((as) => {
    const j = i + d;
    if (j < 0 || j >= as.length) return as;
    const next = [...as];
    [next[i], next[j]] = [next[j], next[i]];
    return next;
  });

  /** « Nouvelle étape… » : créée tout de suite (le serveur donne son identité), câblée dans le brouillon — elle ne
   *  sera joignable qu'une fois cette carte enregistrée. Si l'admin annule, le panneau « Contrôle » la listera
   *  comme injoignable : rien ne se perd en silence. */
  async function createStep(assign: (key: string) => void) {
    const taken = new Set(targets.map((n) => n.key));
    let n = 1;
    while (taken.has(`${phase}-etape-${n}`)) n += 1;
    const newKey = `${phase}-etape-${n}`;
    try {
      const res = await apiFetch<{ data: TutorialNodeDto }>('/tutorial/nodes', {
        method: 'POST',
        body: JSON.stringify({
          key: newKey, category: phase, title: el.newStepTitle, sortOrder: sortOrder + 1,
          card: { ...emptyTutorialCard(), kind: 'end' },
        } satisfies SaveTutorialNodeRequest),
      });
      setExtraNodes((x) => [...x, res.data]);
      assign(res.data.key);
      toast.success(fill(el.created, { key: res.data.key }));
    } catch (err) {
      toast.error((err as Error)?.message ?? 'Error');
    }
  }

  function buildCard(): TutorialCard {
    return {
      ...card,
      short: nullIfBlank(card.short),
      lead: nullIfBlank(card.lead),
      points: lines(pointsText),
      steps: lines(stepsText),
      callouts: card.callouts.filter((c) => c.text.trim()).map((c) => ({ type: c.type, text: c.text.trim() })),
      more: nullIfBlank(card.more),
      moreLabel: nullIfBlank(card.moreLabel),
      checklist: card.checklist.filter((c) => c.label.trim()).map((c) => ({ ...c, label: c.label.trim(), hint: nullIfBlank(c.hint) })),
      checklistHint: nullIfBlank(card.checklistHint),
      examples: list(examplesText).filter((x) => /^\d{4}$/.test(x)),
      window: card.kind === 'window' && card.window?.title.trim()
        ? { bar: card.window.bar.trim(), title: card.window.title.trim(), text: card.window.text.trim() } : null,
      next: card.next && card.next.label.trim()
        ? { label: card.next.label.trim(), targetKey: card.next.targetKey || null, chip: nullIfBlank(card.next.chip) } : null,
      factLabel: nullIfBlank(card.factLabel),
      // N32.7 — toujours envoyée, en booléen : une carte passée en « code » ou « scope » perd la case avec.
      skipWhenCodeDecides: card.skipWhenCodeDecides === true
        && card.kind !== 'code' && card.kind !== 'scope' && card.kind !== 'window' && card.kind !== 'rules',
    };
  }

  async function save() {
    setSaving(true);
    try {
      const body: SaveTutorialNodeRequest = {
        key: key.trim(),
        category: phase,
        title: title.trim(),
        comment: nullIfBlank(comment),
        reference: nullIfBlank(reference),
        isRoot,
        sortOrder,
        answers: answers.filter((a) => a.label.trim()).map((a) => {
          const match = a.match ? Object.fromEntries(Object.entries(a.match).filter(([, v]) => Array.isArray(v) && v.length)) : null;
          return {
            label: a.label.trim(),
            targetKey: a.targetKey || null,
            ...(a.hint?.trim() ? { hint: a.hint.trim() } : {}),
            ...(a.chip?.trim() ? { chip: a.chip.trim() } : {}),
            ...(a.primary ? { primary: true } : {}),
            ...(match && Object.keys(match).length ? { match: match as TutorialAnswerMatch } : {}),
          };
        }),
        card: buildCard(),
      };
      await apiFetch(`/tutorial/nodes/${node.id}`, { method: 'PATCH', body: JSON.stringify(body) });
      toast.success(el.saved);
      await onSaved(node.key, key.trim());
    } catch (err) {
      toast.error((err as Error)?.message ?? 'Error');
    } finally {
      setSaving(false);
    }
  }

  async function remove() {
    setConfirmDelete(false);
    try {
      const res = await apiFetch<{ data: { unwired: number } }>(`/tutorial/nodes/${node.id}`, { method: 'DELETE' });
      toast.success(fill(el.deleted, { n: res.data.unwired }));
      await onDeleted(node.key);
    } catch (err) {
      toast.error((err as Error)?.message ?? 'Error');
    }
  }

  const k = card.kind;
  return (
    <div className="space-y-4" data-tuto-editor={node.key}>
      <h2 className="text-[18px] font-bold text-slate-800">{el.title}</h2>

      <Section title={el.identity}>
        <Field label={el.titleLabel}><Input value={title} onChange={(e) => setTitle(e.target.value)} /></Field>
        <div className="grid gap-3 sm:grid-cols-2">
          <Field label={el.shortLabel}><Input value={card.short ?? ''} onChange={(e) => setC('short', e.target.value)} /></Field>
          <Field label={el.keyLabel}>
            <Input value={key} className="font-mono" onChange={(e) => setKey(e.target.value.toLowerCase().replace(/[^a-z0-9-]/g, '-'))} />
          </Field>
          <Field label={el.phaseLabel}>
            <select value={phase} onChange={(e) => setPhase(e.target.value as TutorialPhase)} className={SELECT_CLASS}>
              {TUTORIAL_PHASES.map((p) => <option key={p} value={p}>{PHASE_SHORT[lang][p]}</option>)}
            </select>
          </Field>
          <Field label={el.kindLabel}>
            <select value={k} onChange={(e) => setC('kind', e.target.value as TutorialCardKind)} className={SELECT_CLASS}>
              {TUTORIAL_CARD_KINDS.map((x) => <option key={x} value={x}>{KIND_LABEL[lang][x]}</option>)}
            </select>
          </Field>
          <Field label={el.orderLabel}>
            <Input type="number" value={sortOrder} className="max-w-[120px]" onChange={(e) => setSortOrder(Number(e.target.value) || 0)} />
          </Field>
          <label className="flex items-center gap-2 self-end pb-2 text-[13px] text-slate-600">
            <input type="checkbox" checked={isRoot} onChange={(e) => setIsRoot(e.target.checked)} /> {el.rootLabel}
          </label>
        </div>
        <div>
          <p className="mb-1 text-[11px] font-semibold text-slate-500">{el.tagsLabel}</p>
          <div className="flex flex-wrap gap-3">
            {TUTORIAL_TAGS.map((t) => (
              <label key={t} className="flex items-center gap-1.5 text-[13px] text-slate-600">
                <input
                  type="checkbox"
                  checked={card.tags.includes(t)}
                  onChange={(e) => setC('tags', e.target.checked ? [...card.tags, t] : card.tags.filter((x) => x !== t))}
                />
                {TAG_SHORT[lang][t]}
              </label>
            ))}
          </div>
        </div>
      </Section>

      <Section title={el.leadLabel}>
        <Field label={el.leadLabel}><Textarea rows={3} value={card.lead ?? ''} onChange={(e) => setC('lead', e.target.value)} /></Field>
        <Field label={el.pointsLabel}><Textarea rows={4} value={pointsText} onChange={(e) => setPointsText(e.target.value)} /></Field>
        <Field label={el.stepsLabel}><Textarea rows={3} value={stepsText} onChange={(e) => setStepsText(e.target.value)} /></Field>
        <div>
          <p className="mb-1 text-[11px] font-semibold text-slate-500">{el.calloutsLabel}</p>
          <div className="space-y-2">
            {card.callouts.map((c, i) => (
              <div key={i} className="grid gap-2 sm:grid-cols-[150px_minmax(0,1fr)_auto]">
                <select
                  value={c.type}
                  className={SELECT_CLASS}
                  onChange={(e) => setC('callouts', card.callouts.map((x, j) => (j === i ? { ...x, type: e.target.value as TutorialCalloutType } : x)))}
                >
                  {TUTORIAL_CALLOUT_TYPES.map((t) => <option key={t} value={t}>{CALLOUT_SHORT[lang][t]}</option>)}
                </select>
                <Textarea
                  rows={2}
                  value={c.text}
                  onChange={(e) => setC('callouts', card.callouts.map((x, j) => (j === i ? { ...x, text: e.target.value } : x)))}
                />
                <Button variant="outline" size="sm" aria-label={el.remove} onClick={() => setC('callouts', card.callouts.filter((_, j) => j !== i))}>
                  <Trash2 className="h-3.5 w-3.5" />
                </Button>
              </div>
            ))}
          </div>
          <Button
            variant="outline"
            size="sm"
            className="mt-2"
            onClick={() => setC('callouts', [...card.callouts, { type: 'todo', text: '' } as TutorialCallout])}
          >
            <Plus className="mr-1.5 h-3.5 w-3.5" /> {el.addCallout}
          </Button>
        </div>
        <Field label={el.moreTitleLabel}><Input value={card.moreLabel ?? ''} onChange={(e) => setC('moreLabel', e.target.value)} /></Field>
        <Field label={el.moreLabel}><Textarea rows={4} value={card.more ?? ''} onChange={(e) => setC('more', e.target.value)} /></Field>
      </Section>

      {(k === 'code') && (
        <Section title={KIND_LABEL[lang].code}>
          <Field label={el.examplesLabel}><Input value={examplesText} className="font-mono" onChange={(e) => setExamplesText(e.target.value)} /></Field>
        </Section>
      )}
      {k === 'window' && (
        <Section title={el.windowLabel}>
          <Field label={el.windowBar}>
            <Input value={card.window?.bar ?? ''} onChange={(e) => setC('window', { bar: e.target.value, title: card.window?.title ?? '', text: card.window?.text ?? '' })} />
          </Field>
          <Field label={el.windowTitle}>
            <Input value={card.window?.title ?? ''} onChange={(e) => setC('window', { bar: card.window?.bar ?? '', title: e.target.value, text: card.window?.text ?? '' })} />
          </Field>
          <Field label={el.windowText}>
            <Textarea rows={3} value={card.window?.text ?? ''} onChange={(e) => setC('window', { bar: card.window?.bar ?? '', title: card.window?.title ?? '', text: e.target.value })} />
          </Field>
        </Section>
      )}

      <Section title={el.checklistLabel}>
        <div className="space-y-2">
          {card.checklist.map((c, i) => (
            <div key={c.id} className="grid gap-2 sm:grid-cols-[minmax(0,1fr)_minmax(0,1fr)_auto_auto] sm:items-center">
              <Input value={c.label} placeholder={el.answerLabel} onChange={(e) => setC('checklist', card.checklist.map((x, j) => (j === i ? { ...x, label: e.target.value } : x)))} />
              <Input value={c.hint ?? ''} placeholder={el.hintLabel} onChange={(e) => setC('checklist', card.checklist.map((x, j) => (j === i ? { ...x, hint: e.target.value } : x)))} />
              <label className="flex items-center gap-1.5 text-[12.5px] text-slate-600">
                <input type="checkbox" checked={c.required} onChange={(e) => setC('checklist', card.checklist.map((x, j) => (j === i ? { ...x, required: e.target.checked } : x)))} />
                {el.required}
              </label>
              <Button variant="outline" size="sm" aria-label={el.remove} onClick={() => setC('checklist', card.checklist.filter((_, j) => j !== i))}>
                <Trash2 className="h-3.5 w-3.5" />
              </Button>
            </div>
          ))}
        </div>
        <Button
          variant="outline"
          size="sm"
          onClick={() => {
            const ids = new Set(card.checklist.map((c) => c.id));
            let n = card.checklist.length + 1;
            while (ids.has(`case-${n}`)) n += 1;
            setC('checklist', [...card.checklist, { id: `case-${n}`, label: '', hint: null, required: true } as TutorialChecklistItem]);
          }}
        >
          <Plus className="mr-1.5 h-3.5 w-3.5" /> {el.addCheck}
        </Button>
        <Field label={el.checklistHintLabel}><Input value={card.checklistHint ?? ''} onChange={(e) => setC('checklistHint', e.target.value)} /></Field>
        <label className="flex items-center gap-2 text-[13px] text-slate-600">
          <input type="checkbox" checked={card.distanceHelper} onChange={(e) => setC('distanceHelper', e.target.checked)} /> {el.distanceLabel}
        </label>
        {/* N32.7 — la case « passer d'office ». Pas sur les cartes « code », « scope » et « fenêtre », qui ont leur
            propre aiguillage, ni sur les règles : le moteur ne la lirait pas, la proposer mentirait. */}
        {card.kind !== 'code' && card.kind !== 'scope' && card.kind !== 'window' && card.kind !== 'rules' && (
          <div>
            <label className="flex items-center gap-2 text-[13px] text-slate-600">
              <input
                type="checkbox"
                checked={card.skipWhenCodeDecides === true}
                onChange={(e) => setC('skipWhenCodeDecides', e.target.checked)}
              /> {el.skipLabel}
            </label>
            <p className="ml-6 mt-0.5 max-w-[70ch] text-[12px] text-slate-400">{el.skipHint}</p>
          </div>
        )}
        <Field label={el.factLabel}><Input value={card.factLabel ?? ''} className="max-w-[240px]" onChange={(e) => setC('factLabel', e.target.value)} /></Field>
      </Section>

      <Section title={el.nextLabel}>
        <label className="flex items-center gap-2 text-[13px] text-slate-600">
          <input
            type="checkbox"
            checked={card.next != null}
            onChange={(e) => setC('next', e.target.checked ? { label: '', targetKey: null, chip: null } : null)}
          />
          {el.nextOn}
        </label>
        {card.next && (
          <div className="grid gap-2 sm:grid-cols-3">
            <Field label={el.nextText}><Input value={card.next.label} onChange={(e) => setC('next', { ...card.next!, label: e.target.value })} /></Field>
            <Field label={el.targetLabel}>
              <TargetSelect
                value={card.next.targetKey}
                nodes={targets}
                lang={lang}
                el={el}
                onChange={(t) => setC('next', { ...card.next!, targetKey: t })}
                onCreate={() => void createStep((kk) => setCard((c) => ({ ...c, next: c.next ? { ...c.next, targetKey: kk } : c.next })))}
              />
            </Field>
            <Field label={el.chipLabel}><Input value={card.next.chip ?? ''} onChange={(e) => setC('next', { ...card.next!, chip: e.target.value })} /></Field>
          </div>
        )}
      </Section>

      <Section title={el.answersLabel}>
        <p className="text-[11px] text-slate-400">{el.answersHint}</p>
        <div className="space-y-3">
          {answers.map((a, i) => (
            <div key={i} className="space-y-2 rounded-lg border border-slate-200 bg-slate-50/60 p-2.5" data-tuto-answer={i}>
              <div className="grid gap-2 sm:grid-cols-[minmax(0,1.4fr)_minmax(0,1fr)_auto]">
                <Input value={a.label} placeholder={el.answerLabel} onChange={(e) => setA(i, { label: e.target.value })} />
                <TargetSelect
                  value={a.targetKey}
                  nodes={targets}
                  lang={lang}
                  el={el}
                  onChange={(t) => setA(i, { targetKey: t })}
                  onCreate={() => void createStep((kk) => setA(i, { targetKey: kk }))}
                />
                <div className="flex gap-1">
                  <Button variant="outline" size="sm" aria-label={el.up} disabled={i === 0} onClick={() => move(i, -1)}><ArrowUp className="h-3.5 w-3.5" /></Button>
                  <Button variant="outline" size="sm" aria-label={el.down} disabled={i === answers.length - 1} onClick={() => move(i, 1)}><ArrowDown className="h-3.5 w-3.5" /></Button>
                  <Button variant="outline" size="sm" aria-label={el.remove} onClick={() => setAnswers((as) => as.filter((_, j) => j !== i))}><Trash2 className="h-3.5 w-3.5" /></Button>
                </div>
              </div>
              <div className="grid gap-2 sm:grid-cols-[minmax(0,1.4fr)_minmax(0,1fr)_auto]">
                <Input value={a.hint ?? ''} placeholder={el.hintLabel} onChange={(e) => setA(i, { hint: e.target.value })} />
                <Input value={a.chip ?? ''} placeholder={el.chipLabel} onChange={(e) => setA(i, { chip: e.target.value })} />
                {k === 'window' && (
                  <label className="flex items-center gap-1.5 text-[12.5px] text-slate-600">
                    <input type="checkbox" checked={!!a.primary} onChange={(e) => setA(i, { primary: e.target.checked })} /> {el.primary}
                  </label>
                )}
              </div>
              <details>
                <summary className="cursor-pointer text-[12px] font-semibold text-brand-rose">
                  {el.matchLabel}{a.match && Object.values(a.match).some((v) => Array.isArray(v) && v.length) ? ' ●' : ''}
                </summary>
                <div className="mt-2 space-y-2">
                  <p className="text-[11px] text-slate-400">{el.matchHint}</p>
                  <div>
                    <p className="mb-1 text-[11px] font-semibold text-slate-500">{el.matchGroups}</p>
                    <div className="flex flex-wrap gap-3">
                      {TUTORIAL_CODE_GROUPS.map((g) => (
                        <label key={g} className="flex items-center gap-1.5 text-[12.5px] text-slate-600">
                          <input
                            type="checkbox"
                            checked={!!a.match?.groups?.includes(g)}
                            onChange={(e) => setM(i, { groups: e.target.checked ? [...(a.match?.groups ?? []), g] : (a.match?.groups ?? []).filter((x) => x !== g) })}
                          />
                          {GROUP_SHORT[lang][g]}
                        </label>
                      ))}
                    </div>
                  </div>
                  <div className="grid gap-2 sm:grid-cols-2">
                    <Field label={el.matchQuadrants}>
                      <Input
                        value={(a.match?.quadrants ?? []).join(', ')}
                        className="font-mono"
                        onChange={(e) => setM(i, { quadrants: list(e.target.value) })}
                      />
                    </Field>
                    <Field label={el.matchCodes}>
                      <Input
                        value={(a.match?.codes ?? []).join(', ')}
                        className="font-mono"
                        onChange={(e) => setM(i, { codes: list(e.target.value).map(Number).filter((n) => Number.isInteger(n) && n >= 0 && n <= 9999) })}
                      />
                    </Field>
                  </div>
                  <div>
                    <p className="mb-1 text-[11px] font-semibold text-slate-500">{el.matchScope}</p>
                    <div className="flex flex-wrap gap-3">
                      {TUTORIAL_SCOPE_OUTCOMES.map((s) => (
                        <label key={s} className="flex items-center gap-1.5 text-[12.5px] text-slate-600">
                          <input
                            type="checkbox"
                            checked={!!a.match?.scope?.includes(s)}
                            onChange={(e) => setM(i, { scope: e.target.checked ? [...(a.match?.scope ?? []), s] : (a.match?.scope ?? []).filter((x) => x !== s) })}
                          />
                          {SCOPE_SHORT[lang][s]}
                        </label>
                      ))}
                    </div>
                  </div>
                </div>
              </details>
            </div>
          ))}
        </div>
        <Button variant="outline" size="sm" onClick={() => setAnswers((as) => [...as, { label: '', targetKey: null }])}>
          <Plus className="mr-1.5 h-3.5 w-3.5" /> {el.addAnswer}
        </Button>
      </Section>

      <Section title={el.commentLabel}>
        <Field label={el.commentLabel}><Textarea rows={3} value={comment} onChange={(e) => setComment(e.target.value)} /></Field>
        <Field label={el.referenceLabel}><Input value={reference} onChange={(e) => setReference(e.target.value)} /></Field>
      </Section>

      <div className="flex flex-wrap items-center justify-between gap-2 border-t border-slate-100 pt-3">
        <span className="text-[11px] text-slate-400">
          {node.updatedBy ? `${el.updatedBy} : ${node.updatedBy} · ` : ''}{new Date(node.updatedAt).toLocaleString()}
        </span>
        <div className="flex flex-wrap gap-2">
          <Button variant="outline" size="sm" onClick={() => setConfirmDelete(true)}>
            <Trash2 className="mr-1.5 h-3.5 w-3.5" /> {el.deleteStep}
          </Button>
          <Button variant="outline" size="sm" onClick={onCancel} disabled={saving}>{el.cancel}</Button>
          <Button size="sm" onClick={() => void save()} disabled={saving || !title.trim() || !/^[a-z0-9][a-z0-9-]*$/.test(key.trim())}>
            {saving ? <Loader2 className="mr-1.5 h-3.5 w-3.5 animate-spin" /> : null}
            {saving ? el.saving : el.save}
          </Button>
        </div>
      </div>

      <ConfirmDialog
        open={confirmDelete}
        tone="rose"
        icon={<AlertTriangle className="h-5 w-5" />}
        title={el.deleteTitle}
        message={el.deleteBody}
        confirmLabel={el.deleteStep}
        cancelLabel={el.cancel}
        onCancel={() => setConfirmDelete(false)}
        onConfirm={() => void remove()}
      />
    </div>
  );
}

// ── Le panneau « Contrôle » ───────────────────────────────────────────────────────────────────────────

export interface TutorialAnomaly {
  key: string;
  text: string;
}

/** Ce qui ne va pas dans le graphe, dit étape par étape : injoignable, issue non câblée ou vers une étape
 *  absente, carte sans issue, carte « code » ou « scope » qui ne route pas tous les cas. */
export function computeAnomalies(nodes: TutorialNodeDto[], lang: Lang): TutorialAnomaly[] {
  const el: ELabels = E[lang];
  const byKey = new Map(nodes.map((n) => [n.key, n]));
  const exits = (n: TutorialNodeDto) => [
    ...n.answers.map((a) => a.targetKey),
    ...(n.card.next ? [n.card.next.targetKey] : []),
  ].filter((t): t is string => !!t);
  const seen = new Set<string>();
  const queue = nodes.filter((n) => n.isRoot && n.card.kind !== 'rules').map((n) => n.key);
  queue.forEach((k) => seen.add(k));
  while (queue.length) {
    const n = byKey.get(queue.shift()!);
    if (!n) continue;
    for (const t of exits(n)) if (byKey.has(t) && !seen.has(t)) { seen.add(t); queue.push(t); }
  }
  const out: TutorialAnomaly[] = [];
  for (const n of nodes) {
    if (n.card.kind === 'rules') continue;
    if (!seen.has(n.key)) out.push({ key: n.key, text: el.unreachable });
    for (const a of n.answers) {
      if (!a.targetKey) out.push({ key: n.key, text: fill(el.unwired, { label: a.label }) });
      else if (!byKey.has(a.targetKey)) out.push({ key: n.key, text: fill(el.missing, { label: a.label, target: a.targetKey }) });
    }
    if (n.card.next) {
      if (!n.card.next.targetKey) out.push({ key: n.key, text: el.nextUnwired });
      else if (!byKey.has(n.card.next.targetKey)) out.push({ key: n.key, text: fill(el.nextMissing, { target: n.card.next.targetKey }) });
    }
    if (n.card.kind !== 'end' && n.answers.length === 0 && !n.card.next) out.push({ key: n.key, text: el.deadEnd });
    if (n.card.kind === 'code') {
      const covered = new Set(n.answers.flatMap((a) => a.match?.groups ?? []));
      const gaps = (['HP_HCA', 'NOT_HP', 'HC', 'BLOCKED'] as TutorialCodeGroup[]).filter((g) => !covered.has(g));
      if (gaps.length) out.push({ key: n.key, text: fill(el.codeGap, { groups: gaps.map((g) => GROUP_SHORT[lang][g]).join(', ') }) });
    }
    if (n.card.kind === 'scope') {
      const covered = new Set(n.answers.flatMap((a) => a.match?.scope ?? []));
      const gaps = TUTORIAL_SCOPE_OUTCOMES.filter((s) => !covered.has(s));
      if (gaps.length) out.push({ key: n.key, text: fill(el.scopeGap, { outcomes: gaps.map((s) => SCOPE_SHORT[lang][s]).join(', ') }) });
    }
    // N32.7 — la case est cochée, mais aucune réponse ne dit à quel code elle correspond : le saut ne se fera
    // jamais, et celui qui a coché la case croit l'inverse.
    if (n.card.skipWhenCodeDecides === true
      && !n.answers.some((a) => a.match?.groups?.length || a.match?.quadrants?.length || a.match?.codes?.length)) {
      out.push({ key: n.key, text: el.skipNoCriteria });
    }
  }
  return out;
}

export function ControlPanel({
  nodes, lang, onOpen,
}: {
  nodes: TutorialNodeDto[];
  lang: Lang;
  onOpen: (key: string) => void;
}) {
  const el: ELabels = E[lang];
  const anomalies = computeAnomalies(nodes, lang);
  const byKey = new Map(nodes.map((n) => [n.key, n]));
  return (
    <div className="rounded-2xl border border-slate-200 bg-white p-4 shadow-sm" data-tuto-control>
      <p className="mb-2 flex items-center gap-2 text-[14px] font-bold text-slate-800">
        <ShieldAlert className="h-4 w-4 text-brand-rose" /> {el.controlTitle}
      </p>
      {anomalies.length === 0 ? (
        <p className="text-[13px] text-emerald-700">{el.controlOk}</p>
      ) : (
        <ul className="space-y-1.5">
          {anomalies.map((a, i) => (
            <li key={i} className="flex flex-wrap items-center gap-2 text-[13px] text-slate-700">
              <button type="button" onClick={() => onOpen(a.key)} className="font-semibold text-brand-rose hover:underline">
                {(byKey.get(a.key)?.card.short || byKey.get(a.key)?.title || a.key)}
              </button>
              <span className="font-mono text-[11px] text-slate-400">{a.key}</span>
              <span>— {a.text}</span>
            </li>
          ))}
        </ul>
      )}
    </div>
  );
}

export const EDITOR_LABELS = E;
